import { useState, useCallback, useRef, useMemo, useEffect } from 'react'

interface UseChartZoomOptions {
  /** The full dataset array */
  data: any[]
  /** The key in each data point used for the x-axis (e.g., 'time') */
  dataKey: string
  /** Minimum number of visible data points when zoomed in */
  minVisiblePoints?: number
  /** Zoom speed factor (0-1, default 0.2) */
  zoomSpeed?: number
}

interface UseChartZoomReturn {
  /** Filtered data to display (zoomed subset or full data) */
  displayData: any[]
  /** Whether the chart is currently zoomed */
  isZoomed: boolean
  /** Reset zoom to show all data */
  resetZoom: () => void
  /** Ref to attach to the chart container div (for wheel events) */
  containerRef: React.RefObject<HTMLDivElement>
  /** Props to spread onto the container div */
  containerProps: {
    ref: React.RefObject<HTMLDivElement>
  }
  /** Drag-to-zoom: onMouseDown handler for the Recharts chart */
  handleMouseDown: (e: any) => void
  /** Drag-to-zoom: onMouseMove handler for the Recharts chart */
  handleMouseMove: (e: any) => void
  /** Drag-to-zoom: onMouseUp handler for the Recharts chart */
  handleMouseUp: () => void
  /** Whether the user is currently dragging to select a zoom area */
  isDragging: boolean
  /** Left edge of the drag selection (for ReferenceArea) */
  refAreaLeft: number | string | null
  /** Right edge of the drag selection (for ReferenceArea) */
  refAreaRight: number | string | null
  /** Cursor style based on drag state */
  cursorStyle: string
}

export function useChartZoom({
  data,
  dataKey,
  minVisiblePoints = 3,
  zoomSpeed = 0.2,
}: UseChartZoomOptions): UseChartZoomReturn {
  // Zoom window as indices into the data array
  const [zoomStart, setZoomStart] = useState<number | null>(null)
  const [zoomEnd, setZoomEnd] = useState<number | null>(null)

  // Drag-to-zoom state — use both state (for rendering ReferenceArea) and
  // refs (so stable callbacks always read the latest values).
  const [refAreaLeft, setRefAreaLeft] = useState<number | string | null>(null)
  const [refAreaRight, setRefAreaRight] = useState<number | string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const refAreaLeftRef = useRef<number | string | null>(null)
  const refAreaRightRef = useRef<number | string | null>(null)
  const isDraggingRef = useRef(false)

  // Keep refs in sync with state
  const updateRefAreaLeft = useCallback((v: number | string | null) => {
    refAreaLeftRef.current = v
    setRefAreaLeft(v)
  }, [])
  const updateRefAreaRight = useCallback((v: number | string | null) => {
    refAreaRightRef.current = v
    setRefAreaRight(v)
  }, [])
  const updateIsDragging = useCallback((v: boolean) => {
    isDraggingRef.current = v
    setIsDragging(v)
  }, [])

  const containerRef = useRef<HTMLDivElement>(
    null!,
  ) as React.RefObject<HTMLDivElement>

  const isZoomed = zoomStart !== null && zoomEnd !== null

  const displayData = useMemo(() => {
    if (!isZoomed || !data.length) return data
    return data.slice(zoomStart!, zoomEnd! + 1)
  }, [data, isZoomed, zoomStart, zoomEnd])

  // Scroll-to-zoom on x-axis — stored in a ref so the non-passive listener
  // always sees the latest state without needing to re-attach.
  const wheelHandler = useRef<(e: WheelEvent) => void>(() => {})
  wheelHandler.current = (e: WheelEvent) => {
    if (!data.length) return
    e.preventDefault()

    const totalPoints = data.length
    const currentStart = zoomStart ?? 0
    const currentEnd = zoomEnd ?? totalPoints - 1
    const currentRange = currentEnd - currentStart

    const rect = containerRef.current?.getBoundingClientRect()
    const mouseXRatio = rect
      ? Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      : 0.5

    const adaptiveMultiplier =
      totalPoints > 100
        ? 1 + Math.min(2.5, (totalPoints - 30) / 80)
        : totalPoints > 30
          ? 1 + (totalPoints - 30) / 120
          : 1
    const effectiveSpeed = zoomSpeed * adaptiveMultiplier

    const rawDelta = Math.round(currentRange * effectiveSpeed)
    const minDelta = Math.max(Math.ceil(totalPoints / 100), 2)
    const zoomDelta = Math.max(minDelta, rawDelta)
    const delta = e.deltaY > 0 ? zoomDelta : -zoomDelta

    const newRange = currentRange + delta
    if (newRange < minVisiblePoints - 1) return
    if (newRange >= totalPoints - 1) {
      setZoomStart(null)
      setZoomEnd(null)
      return
    }

    const leftDelta = Math.round(delta * mouseXRatio)
    const rightDelta = delta - leftDelta

    let newStart = currentStart - leftDelta
    let newEnd = currentEnd + rightDelta

    if (newStart < 0) {
      newEnd += -newStart
      newStart = 0
    }
    if (newEnd >= totalPoints) {
      newStart -= newEnd - (totalPoints - 1)
      newEnd = totalPoints - 1
    }
    newStart = Math.max(0, newStart)
    newEnd = Math.min(totalPoints - 1, newEnd)

    setZoomStart(newStart)
    setZoomEnd(newEnd)
  }

  // Attach a non-passive wheel listener so preventDefault() actually works
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handler = (e: WheelEvent) => wheelHandler.current(e)
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [])

  // Keep a ref to the latest data + options so stable callbacks can access them
  const dataRef = useRef(data)
  dataRef.current = data
  const dataKeyRef = useRef(dataKey)
  dataKeyRef.current = dataKey
  const minVisiblePointsRef = useRef(minVisiblePoints)
  minVisiblePointsRef.current = minVisiblePoints

  // Drag-to-zoom handlers — stable references that read from refs
  const handleMouseDown = useCallback(
    (e: any) => {
      if (e && e.activeLabel !== undefined) {
        updateRefAreaLeft(e.activeLabel)
        updateRefAreaRight(null)
        updateIsDragging(true)
      }
    },
    [updateRefAreaLeft, updateRefAreaRight, updateIsDragging],
  )

  const handleMouseMove = useCallback(
    (e: any) => {
      if (isDraggingRef.current && e && e.activeLabel !== undefined) {
        updateRefAreaRight(e.activeLabel)
      }
    },
    [updateRefAreaRight],
  )

  const handleMouseUp = useCallback(() => {
    const left = refAreaLeftRef.current
    const right = refAreaRightRef.current

    if (left !== null && right !== null && left !== right) {
      const currentData = dataRef.current
      const key = dataKeyRef.current
      const minPts = minVisiblePointsRef.current

      const leftIdx = currentData.findIndex(
        (d: any) => d[key] === left || d[key] >= left,
      )
      const rightIdx = currentData.findIndex(
        (d: any) => d[key] === right || d[key] >= right,
      )

      if (leftIdx !== -1 && rightIdx !== -1) {
        const start = Math.min(leftIdx, rightIdx)
        const end = Math.max(leftIdx, rightIdx)
        if (end - start >= minPts - 1) {
          setZoomStart(start)
          setZoomEnd(end)
        }
      }
    }
    updateRefAreaLeft(null)
    updateRefAreaRight(null)
    updateIsDragging(false)
  }, [updateRefAreaLeft, updateRefAreaRight, updateIsDragging])

  const resetZoom = useCallback(() => {
    setZoomStart(null)
    setZoomEnd(null)
    // Also clear any in-progress drag to prevent stale mouseUp from re-zooming
    updateRefAreaLeft(null)
    updateRefAreaRight(null)
    updateIsDragging(false)
  }, [updateRefAreaLeft, updateRefAreaRight, updateIsDragging])

  return {
    displayData,
    isZoomed,
    resetZoom,
    containerRef,
    containerProps: {
      ref: containerRef,
    },
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    isDragging,
    refAreaLeft,
    refAreaRight,
    cursorStyle: isDragging ? 'col-resize' : 'crosshair',
  }
}
